import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsRightLeftIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import { accountsAPI } from '@/services/api'
import { useToast } from '@/hooks/useToast'
import ToastContainer from '@/components/ui/ToastContainer'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts'

interface Transaction {
  id: number
  type: string
  amount: number
  description: string
  date: string
  category_name?: string
  notes?: string
  balance_after: number
  is_transfer: boolean
}

interface Account {
  id: string
  name: string
  type: string
  bank: string
  balance: string
}

const AccountStatement: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>()
  const navigate = useNavigate()
  const { toasts, success, error, removeToast } = useToast()

  const [account, setAccount] = useState<Account | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([])

  // Filters
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [transactionType, setTransactionType] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    if (accountId) {
      fetchAccountAndTransactions()
    }
  }, [accountId])

  useEffect(() => {
    applyFilters()
  }, [transactions, startDate, endDate, transactionType])

  const fetchAccountAndTransactions = async () => {
    setLoading(true)
    try {
      // Fetch account details
      const accountsData = await accountsAPI.list()
      const foundAccount = accountsData.find((acc: Account) => acc.id === parseInt(accountId!))

      if (!foundAccount) {
        error('Conta não encontrada', 'A conta solicitada não existe')
        navigate('/accounts')
        return
      }

      setAccount(foundAccount)

      // Fetch transactions
      const result = await accountsAPI.getTransactions(parseInt(accountId), {
        limit: 500
      })
      setTransactions(result.transactions || [])
    } catch (err: any) {
      error('Erro ao carregar dados', err.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...transactions]

    if (startDate) {
      filtered = filtered.filter(t => new Date(t.date) >= new Date(startDate))
    }

    if (endDate) {
      filtered = filtered.filter(t => new Date(t.date) <= new Date(endDate))
    }

    if (transactionType) {
      if (transactionType === 'TRANSFER') {
        filtered = filtered.filter(t => t.is_transfer)
      } else {
        filtered = filtered.filter(t => t.type === transactionType && !t.is_transfer)
      }
    }

    setFilteredTransactions(filtered)
  }

  const clearFilters = () => {
    setStartDate('')
    setEndDate('')
    setTransactionType('')
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const exportToCSV = () => {
    const csvContent = [
      ['Data', 'Descrição', 'Tipo', 'Categoria', 'Valor', 'Saldo'],
      ...filteredTransactions.map(t => [
        formatDate(t.date),
        t.description || '',
        t.is_transfer ? 'Transferência' : t.type === 'INCOME' ? 'Receita' : 'Despesa',
        t.category_name || '',
        t.type === 'INCOME' ? formatCurrency(t.amount) : formatCurrency(-t.amount),
        formatCurrency(t.balance_after)
      ])
    ].map(row => row.join(';')).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `extrato_${account?.name}_${new Date().toISOString().split('T')[0]}.csv`
    link.click()

    success('Extrato exportado!', 'Arquivo CSV gerado com sucesso')
  }

  // Prepare chart data
  const chartData = filteredTransactions
    .slice()
    .reverse()
    .map(t => ({
      date: formatDate(t.date),
      saldo: parseFloat(t.balance_after.toString()),
      receita: t.type === 'INCOME' && !t.is_transfer ? parseFloat(t.amount.toString()) : 0,
      despesa: t.type === 'EXPENSE' && !t.is_transfer ? parseFloat(t.amount.toString()) : 0
    }))

  // Calculate statistics
  const totalIncome = filteredTransactions
    .filter(t => t.type === 'INCOME' && !t.is_transfer)
    .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0)

  const totalExpense = filteredTransactions
    .filter(t => t.type === 'EXPENSE' && !t.is_transfer)
    .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0)

  const totalTransfers = filteredTransactions.filter(t => t.is_transfer).length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!account) {
    return null
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/accounts')}
              className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Extrato Detalhado</h1>
              <p className="text-gray-600">{account.name} - {account.bank}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showFilters
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FunnelIcon className="w-5 h-5" />
              Filtros
            </button>
            <button
              onClick={exportToCSV}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="border-t pt-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Final
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo
                </label>
                <select
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todos</option>
                  <option value="INCOME">Receitas</option>
                  <option value="EXPENSE">Despesas</option>
                  <option value="TRANSFER">Transferências</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Saldo Atual</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(parseFloat(account.balance))}</p>
            </div>
            <CalendarIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Total Receitas</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(totalIncome)}</p>
            </div>
            <ArrowDownIcon className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Total Despesas</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(totalExpense)}</p>
            </div>
            <ArrowUpIcon className="w-8 h-8 text-red-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Transferências</p>
              <p className="text-2xl font-bold mt-1">{totalTransfers}</p>
            </div>
            <ArrowsRightLeftIcon className="w-8 h-8 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Balance Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Evolução do Saldo</h3>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorSaldo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Area
                type="monotone"
                dataKey="saldo"
                stroke="#3B82F6"
                fillOpacity={1}
                fill="url(#colorSaldo)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-gray-500 py-12">Nenhum dado para exibir</p>
        )}
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Transações ({filteredTransactions.length})
        </h3>

        {filteredTransactions.length === 0 ? (
          <p className="text-center text-gray-500 py-12">Nenhuma transação encontrada</p>
        ) : (
          <div className="space-y-3">
            {filteredTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className={`border rounded-lg p-4 ${
                  transaction.is_transfer
                    ? 'bg-blue-50 border-blue-200'
                    : transaction.type === 'INCOME'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        transaction.is_transfer
                          ? 'bg-blue-100 text-blue-600'
                          : transaction.type === 'INCOME'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-red-100 text-red-600'
                      }`}
                    >
                      {transaction.is_transfer ? (
                        <ArrowsRightLeftIcon className="w-5 h-5" />
                      ) : transaction.type === 'INCOME' ? (
                        <ArrowDownIcon className="w-5 h-5" />
                      ) : (
                        <ArrowUpIcon className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {transaction.description || 'Sem descrição'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">{formatDate(transaction.date)}</p>
                      {transaction.category_name && (
                        <span className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                          {transaction.category_name}
                        </span>
                      )}
                      {transaction.notes && (
                        <p className="text-xs text-gray-500 mt-2">{transaction.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-lg font-semibold ${
                        transaction.type === 'INCOME' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {transaction.type === 'INCOME' ? '+' : '-'}{' '}
                      {formatCurrency(parseFloat(transaction.amount.toString()))}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Saldo: {formatCurrency(parseFloat(transaction.balance_after.toString()))}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}

export default AccountStatement
